import { useState } from 'react';

import Alert from '@/components/Alert';
import Button from '@/components/Button';
import Card from '@/components/Card';
import FormElement from '@/components/FormElement';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import TextArea from '@/components/TextArea';
import { Body } from '@/components/Typography';
import { NoshowAlert } from '@/types/noshowAlerts';

interface ResolveModalProps {
  alert: NoshowAlert;
  onClose: () => void;
  onResolve: (id: number, notes: string) => Promise<void>;
}

const ResolveModal = ({ alert, onClose, onResolve }: ResolveModalProps) => {
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onResolve(alert.id, notes);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.errors?.[0] || 'Failed to resolve alert');
      setLoading(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title={`Resolve Alert #${alert.id}`}
      onRequestClose={onClose}
    >
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon="warning"
            status="danger"
            title="Error"
          />
        )}
        <Stack vertical gap={8}>
          <Body weight="medium">Worker: {alert.worker?.name || 'N/A'}</Body>
          <Body weight="medium">
            Supervisor: {alert.supervisor?.name || 'N/A'}
          </Body>
          <Body weight="medium">Customer: {alert.customer_name || 'N/A'}</Body>
        </Stack>
        <FormElement label="Notes">
          <TextArea
            placeholder="Add resolution notes..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </FormElement>
      </Card.Section>
      <Card.Section>
        <Stack justify="end">
          <Button
            a11yLabel="Cancel"
            appearance="outline"
            label="Cancel"
            onClick={onClose}
          />
          <Button
            a11yLabel="Resolve"
            isLoading={loading}
            label="Resolve"
            onClick={handleSubmit}
          />
        </Stack>
      </Card.Section>
    </Modal>
  );
};

export default ResolveModal;
